import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, ChevronLeft, Building2, Bell, HelpCircle } from "lucide-react";
import ContextSelector from "@/components/ContextSelector";

const ROUTE_LABELS = {
  "/dashboard": "لوحة القيادة",
  "/schools": "إدارة المدارس",
  "/students": "الطلاب",
  "/subjects": "المواد الدراسية",
  "/templates": "قوالب التقييم",
  "/grades/year-work": "رصد أعمال السنة",
  "/grades/final-exam": "رصد الاختبار النهائي",
  "/certificates": "الشهادات",
  "/reports": "مراجعة الرصد",
  "/analysis": "التحليل الإحصائي",
  "/print/class-lists": "طباعة قوائم الفصول",
  "/print/grade-sheets": "طباعة كشوف الرصد",
  "/settings": "إعدادات المدرسة",
  "/support": "الدعم الفني",
};

export default function Topbar() {
  const { user, logout } = useAuth();
  const { ctx } = useApp();
  const isSuper = user?.role === "super_admin";
  const { pathname } = useLocation();
  const currentLabel = ROUTE_LABELS[pathname] || "";
  const initials = (user?.name || "?")
    .split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header
      className="sticky top-0 z-30 bg-white"
      style={{ borderBottom: "1px solid #EEF3EF" }}
      data-testid="app-topbar"
    >
      <div className="flex items-center justify-between gap-6 px-8 h-[62px]">
        {/* Right (RTL start): breadcrumb + context */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {isSuper ? (
            <div className="flex items-center gap-3">
              <div className="icon-chip h-9 w-9">
                <Building2 className="h-4 w-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-[10.5px] font-bold text-slate-400 tracking-[0.14em] uppercase">المنصّة</div>
                <div className="font-display text-[14.5px] font-bold text-slate-900 mt-0.5">لوحة الإشراف</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-5 min-w-0">
              <div className="hidden md:flex items-center gap-2 text-[12.5px] shrink-0">
                <span className="text-slate-400">الرئيسية</span>
                <ChevronLeft className="h-3.5 w-3.5 text-slate-300" strokeWidth={1.75} />
                <span className="font-semibold text-slate-900">{currentLabel}</span>
              </div>
              <div className="h-6 w-px bg-slate-200 hidden md:block" />
              <ContextSelector />
            </div>
          )}
        </div>

        {/* Left (RTL end): actions + user */}
        <div className="flex items-center gap-2 shrink-0">
          <button className="hidden md:flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:text-[#0E7C6F] hover:bg-slate-50 transition-colors">
            <HelpCircle className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button className="hidden md:flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:text-[#0E7C6F] hover:bg-slate-50 transition-colors relative">
            <Bell className="h-4 w-4" strokeWidth={1.75} />
            <span className="absolute top-2 end-2 h-1.5 w-1.5 rounded-full bg-[#DAA520]" />
          </button>
          <div className="h-6 w-px bg-slate-200 mx-1" />
          <div className="text-end hidden md:block">
            <div className="text-[12.5px] font-semibold text-slate-900 leading-tight">
              {user?.name || "مستخدم"}
            </div>
            <div className="text-[10.5px] text-slate-500 mt-0.5">
              {isSuper ? "Super Admin" : (user?.role === "admin" ? "مدير النظام" : "معلّم")}
              {!isSuper && ctx?.year && ` · ${ctx.year}`}
            </div>
          </div>
          <div
            className="h-9 w-9 rounded-xl flex items-center justify-center text-[11.5px] font-bold text-white shrink-0"
            style={{ background: "linear-gradient(135deg, #0E7C6F 0%, #14B8A6 100%)" }}
          >
            {initials}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="logout-btn"
            className="h-9 px-2.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </header>
  );
}
